import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../utils/auth.js';
import { getApiBaseUrl } from '../utils/apiBase';
import KnowledgeUpload from './KnowledgeUpload';
import MetricsDashboard from './MetricsDashboard';
import TrialBanner from './TrialBanner';
import SubscriptionMenuButton from './SubscriptionMenuButton.jsx';
import SubscriptionCenterModal from './SubscriptionCenterModal.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import TourMode from './tour/TourMode.jsx';
import TourButton from './tour/TourButton.jsx';
import HelpMenu, { FeedbackButton } from './help/HelpMenu.jsx';
import { usePageTour } from './tour/usePageTour.js';
import {
  FiAlertTriangle, FiArrowLeft, FiBarChart2, FiBox, FiCpu, FiDatabase, FiEdit2, FiFileText,
  FiGrid, FiHardDrive, FiImage, FiInbox, FiLayers, FiLogOut, FiMail, FiMessageCircle,
  FiMessageSquare, FiPlus, FiRefreshCw, FiServer, FiShield, FiThumbsDown, FiThumbsUp,
  FiTrash2, FiUploadCloud, FiUsers, FiZap
} from 'react-icons/fi';
import '../styles/admin-dashboard.css';
import { confirmDialog } from './ConfirmDialog.jsx';

// Counts up to a numeric value on mount/change. Non-numeric values render as-is.
function AnimatedNumber({ value }) {
  const target = Number(value);
  const isNumeric = value !== null && value !== undefined && value !== '' && Number.isFinite(target);
  const [display, setDisplay] = useState(isNumeric ? 0 : value);

  useEffect(() => {
    if (!isNumeric) {
      setDisplay(value);
      return undefined;
    }
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || target === 0) {
      setDisplay(target);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const duration = 900;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, target, isNumeric]);

  return <>{isNumeric ? Number(display).toLocaleString() : display}</>;
}

function SkeletonRows({ label, rows = 4 }) {
  return (
    <div className="admin-loading admin-skeleton" role="status" aria-live="polite">
      <span className="admin-sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="admin-skeleton-row" key={i} aria-hidden="true">
          <span className="nb-skeleton" />
          <span className="nb-skeleton" />
          <span className="nb-skeleton" />
          <span className="nb-skeleton" />
        </div>
      ))}
    </div>
  );
}

// Guided tour of the admin console. Targets are data-tour attributes on
// elements that are always rendered (nav items, header actions).
function buildAdminTourSteps({ isMobile, showLogo, showTenants }) {
  if (isMobile) {
    return [
      { target: 'admin-nav', title: 'Your admin console', body: 'Swipe through these sections: overview, users, documents, feedback, metrics' + (showLogo ? ' and brand logo.' : '.') },
      showTenants && { target: 'admin-tenants', title: 'Pick a tenant', body: 'Choose which company you are managing. Users and documents follow the tenant you select here.' },
      { target: 'admin-content', title: 'Your workspace', body: 'The selected section opens here, with stats, tables and upload tools sized for your screen.' },
      { target: 'admin-header-actions', title: 'Quick controls', body: 'Replay this tour, switch light or dark mode, manage your plan, or jump back to chat.' }
    ].filter(Boolean);
  }
  return [
    { target: 'admin-nav', title: 'Your admin console', body: 'Every admin area lives in this sidebar. Pick a section and the workspace on the right updates instantly.' },
    showTenants && { target: 'admin-tenants', title: 'Pick a tenant', body: 'As a super admin you can switch between companies here. Users, documents and stats follow the tenant you select.' },
    { target: 'admin-nav-documents', title: 'Teach NovaBotX', body: 'Upload PDFs, Word and Excel files, images, websites or pasted text, and choose which departments can retrieve them.' },
    { target: 'admin-nav-users', title: 'Manage your people', body: 'Add teammates, change roles between user and admin, and remove accounts that no longer need access.' },
    { target: 'admin-nav-metrics', title: 'Track performance', body: 'Response times, satisfaction, cost and user analytics, so you can see who is active and what needs attention.' },
    showLogo
      ? { target: 'admin-nav-logo', title: 'Make it yours', body: 'Upload your company logo and it appears in the chat sidebar for everyone in your organisation.' }
      : { target: 'admin-nav-feedback', title: 'Hear from users', body: 'Ratings, written reviews and thumbs up/down votes from real conversations land here.' },
    { target: 'admin-header-actions', title: 'Quick controls', body: 'Replay this tour any time, flip between light and dark mode, manage your subscription, or jump back to chat.' }
  ].filter(Boolean);
}

function AdminDashboard() {
  const navigate = useNavigate();
  const API_BASE_URL = getApiBaseUrl();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTenant, setSelectedTenant] = useState(null); // For super admin tenant tabs
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [showCreateTenantForm, setShowCreateTenantForm] = useState(false);
  const [showEditTenantForm, setShowEditTenantForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [tenantStats, setTenantStats] = useState(null);
  const [showDemoteModal, setShowDemoteModal] = useState(false);
  const [demoteUserData, setDemoteUserData] = useState(null);
  const [demoteTargetTenant, setDemoteTargetTenant] = useState('');
  const [showWelcomeMessageEditor, setShowWelcomeMessageEditor] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [editingWelcomeMessageFor, setEditingWelcomeMessageFor] = useState(null);
  const [showContactEmailsEditor, setShowContactEmailsEditor] = useState(false);
  const [hrContactEmail, setHrContactEmail] = useState('');
  const [financeContactEmail, setFinanceContactEmail] = useState('');
  const [editingContactEmailsFor, setEditingContactEmailsFor] = useState(null);
  const [uploadUsage, setUploadUsage] = useState(null);
  const [adminTenantStats, setAdminTenantStats] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [voteFeedback, setVoteFeedback] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [showSubscriptionCenter, setShowSubscriptionCenter] = useState(false);
  const [subscriptionCenterTab, setSubscriptionCenterTab] = useState('billing');
  const { touring, start: startTour, end: endTour } = usePageTour();

  const canDownloadDocuments = (() => {
    const allow = new Set([
      'yashwanth.setty@novalink-solutions.com',
      'shivam.singh@novalink-solutions.com',
      'binson.le@novalink-solutions.com'
    ]);
    const email = String(currentUser?.email || '').trim().toLowerCase();
    return allow.has(email);
  })();

  // Form state
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user',
    companyId: ''
  });

  const [tenantFormData, setTenantFormData] = useState({
    companyId: '',
    companyName: '',
    domain: '',
    adminEmail: '',
    adminPassword: '',
    adminFirstName: '',
    adminLastName: ''
  });

  useEffect(() => {
    const checkAdminAccess = async () => {
      // Check authentication
      if (!authService.isAuthenticated()) {
        navigate('/login');
        return;
      }

      // Check admin and super admin status
      try {
        const [adminStatus, superAdminStatus] = await Promise.all([
          authService.checkAdminStatus(),
          authService.checkSuperAdminStatus()
        ]);

        setIsAdmin(adminStatus);
        setIsSuperAdmin(superAdminStatus);

        if (!adminStatus && !superAdminStatus) {
          navigate('/');
          return;
        }

        // User is admin or super admin, proceed with loading data
        loadCurrentUser().then((user) => {
          // After currentUser loads, load tenant stats for regular admins
          if (adminStatus && !superAdminStatus && user?.companyId) {
            loadAdminTenantStats();
          }
        });
        loadUploadUsage();
        
        if (superAdminStatus) {
          loadTenants();
        } else {
          // Regular admin - load their tenant data
          if (activeTab === 'users') {
            loadUsers();
          } else if (activeTab === 'documents') {
            loadDocuments();
          } else if (activeTab === 'feedback') {
            loadFeedback();
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/');
        return;
      }
    };

    checkAdminAccess();
  }, [activeTab, navigate, selectedTenant]);

  // Load data when tenant is selected (super admin)
  useEffect(() => {
    if (isSuperAdmin) {
      if (activeTab === 'overview') {
        return;
      }
      if (selectedTenant === 'system') {
        // Load system database users
        if (activeTab === 'users') {
          loadSystemUsers();
        } else if (activeTab === 'documents') {
          // System database doesn't have documents, show empty or message
          setDocuments([]);
        } else if (activeTab === 'feedback') {
          loadFeedback();
        }
      } else if (selectedTenant) {
        loadTenantStats(selectedTenant);
        if (activeTab === 'users') {
          loadUsersForTenant(selectedTenant);
        } else if (activeTab === 'documents') {
          loadDocumentsForTenant(selectedTenant);
        } else if (activeTab === 'feedback') {
          loadFeedback();
        }
      } else if (activeTab === 'users') {
        setUsers([]);
        setError('Select a tenant tab to view users');
      } else if (activeTab === 'feedback') {
        loadFeedback();
      }
    }
  }, [selectedTenant, activeTab, isSuperAdmin]);

  // Load tenant stats when currentUser changes (regular admin)
  useEffect(() => {
    if (isAdmin && !isSuperAdmin && currentUser?.companyId) {
      loadAdminTenantStats();
    }
  }, [currentUser, isAdmin, isSuperAdmin]);

  // Load current logo
  const loadCurrentLogo = async () => {
    if (!currentUser?.companyId) return;
    
    setLoadingLogo(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${currentUser.companyId}`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success' && data.data?.tenant?.settings?.customBranding?.logoUrl) {
        setCurrentLogoUrl(data.data.tenant.settings.customBranding.logoUrl);
      } else {
        setCurrentLogoUrl(null);
      }
    } catch (err) {
      console.error('Error loading logo:', err);
      setCurrentLogoUrl(null);
    } finally {
      setLoadingLogo(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'logo' && currentUser?.companyId) {
      loadCurrentLogo();
    }
  }, [activeTab, currentUser?.companyId]);

  // Handle logo file selection
  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please select a JPG, PNG, GIF, or WebP image.');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB limit.');
        return;
      }

      setLogoFile(file);
      setError(null);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle logo save
  const handleSaveLogo = async () => {
    if (!logoFile || !currentUser?.companyId) return;

    setLoadingLogo(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      // Get auth headers but exclude Content-Type for FormData (browser will set it with boundary)
      const authHeaders = authService.getAuthHeaders();
      const headers = {};
      if (authHeaders['Authorization']) {
        headers['Authorization'] = authHeaders['Authorization'];
      }

      const response = await fetch(`${API_BASE_URL}/api/tenant/${currentUser.companyId}/logo`, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('Logo updated successfully');
        setCurrentLogoUrl(data.data.logoUrl);
        setLogoFile(null);
        setLogoPreview(null);
        const input = document.getElementById('logo-file-input');
        if (input) input.value = '';
        
        // Dispatch event for ChatPage to update logo
        window.dispatchEvent(new CustomEvent('tenant-logo-updated', {
          detail: { companyId: currentUser.companyId, logoUrl: data.data.logoUrl }
        }));

        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to update logo');
      }
    } catch (err) {
      setError('Error updating logo: ' + err.message);
    } finally {
      setLoadingLogo(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const profile = await authService.getProfile();
      setCurrentUser(profile.data?.user);
      return profile.data?.user;
    } catch (err) {
      console.error('Failed to load current user:', err);
      return null;
    }
  };

  const loadUploadUsage = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/upload-usage`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setUploadUsage(data.usage);
      }
    } catch (err) {
      console.error('Failed to load upload usage:', err);
    }
  };

  const loadAdminTenantStats = async () => {
    if (!currentUser?.companyId) {
      // Wait for currentUser to load
      return;
    }
    try {
      // Use qdrant-info endpoint to get vector count
      const response = await fetch(`${API_BASE_URL}/api/knowledge/qdrant-info`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success && data.collection) {
        // Also get document count from list
        const docResponse = await fetch(`${API_BASE_URL}/api/knowledge/list`, {
          headers: authService.getAuthHeaders()
        });
        const docData = await docResponse.json();
        const documentCount = docData.success ? (docData.files || []).length : 0;
        
        // Get user count using the regular admin endpoint (not superadmin)
        let userCount = 0;
        try {
          const userResponse = await fetch(`${API_BASE_URL}/api/auth/users`, {
            headers: authService.getAuthHeaders()
          });
          const userData = await userResponse.json();
          userCount = userData.status === 'success' ? (userData.data?.users || []).length : 0;
        } catch (userErr) {
          // If user endpoint fails, just set to 0 and continue
          console.warn('Failed to get user count for stats:', userErr);
        }
        
        setAdminTenantStats({
          vectorCount: data.collection.pointsCount || 0,
          documentCount: documentCount,
          userCount: userCount,
          collectionName: data.collection.name
        });
      }
    } catch (err) {
      console.error('Failed to load admin tenant stats:', err);
    }
  };

  const loadFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/metrics/reviews`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setFeedback(data.reviews || []);
        setVoteFeedback(data.voteFeedback || null);
      } else {
        setError(data.message || 'Failed to load feedback');
      }
    } catch (err) {
      setError('Error loading feedback: ' + err.message);
      console.error('Error loading feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFeedback = async (index) => {
    if (!(await confirmDialog({
      title: 'Delete this review?',
      message: 'The rating and comment will be removed from User feedback.',
      confirmLabel: 'Delete review',
      tone: 'danger',
    }))) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/metrics/reviews/${index}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Feedback deleted successfully');
        // Reload feedback to get updated list
        loadFeedback();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to delete feedback');
      }
    } catch (err) {
      setError('Error deleting feedback: ' + err.message);
      console.error('Error deleting feedback:', err);
    }
  };

  const loadTenants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success') {
        setTenants(data.data.tenants || []);
        // Auto-select first tenant if available
        if (data.data.tenants && data.data.tenants.length > 0 && !selectedTenant) {
          setSelectedTenant(data.data.tenants[0].companyId);
        }
      } else {
        setError(data.message || 'Failed to load tenants');
      }
    } catch (err) {
      setError('Error loading tenants: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/users`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success') {
        setUsers(data.data.users);
      } else {
        setError(data.message || 'Failed to load users');
      }
    } catch (err) {
      setError('Error loading users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/users`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success') {
        setUsers(data.data.users);
      } else {
        setError(data.message || 'Failed to load users');
      }
    } catch (err) {
      setError('Error loading users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsersForTenant = async (companyId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/users?tenantId=${companyId}`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success') {
        setUsers(data.data.users);
      } else {
        setError(data.message || 'Failed to load users');
      }
    } catch (err) {
      setError('Error loading users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load only system database users
      const response = await fetch(`${API_BASE_URL}/api/super-admin/users?systemOnly=true`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success') {
        setUsers(data.data.users || []);
      } else {
        setError(data.message || 'Failed to load users');
      }
    } catch (err) {
      setError('Error loading users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/list`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setDocuments(data.files || []);
      } else {
        setError('Failed to load documents');
      }
    } catch (err) {
      setError('Error loading documents: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentsForTenant = async (companyId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/documents?companyId=${companyId}`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDocuments(data.data.documents || []);
      } else {
        setError(data.message || 'Failed to load documents');
      }
    } catch (err) {
      setError('Error loading documents: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(tenantFormData)
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('Tenant created successfully');
        setShowCreateTenantForm(false);
        setTenantFormData({
          companyId: '',
          companyName: '',
          domain: '',
          adminEmail: '',
          adminPassword: '',
          adminFirstName: '',
          adminLastName: ''
        });
        loadTenants();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to create tenant');
      }
    } catch (err) {
      setError('Error creating tenant: ' + err.message);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      // Special case: Superadmin creating another superadmin.
      // Superadmin accounts live in the system DB and are not tied to a tenant,
      // so we call the promote endpoint instead of the tenant-scoped user create.
      if (isSuperAdmin && userFormData.role === 'superadmin') {
        const promoteResponse = await fetch(`${API_BASE_URL}/api/super-admin/promote`, {
          method: 'POST',
          headers: authService.getAuthHeaders(),
          body: JSON.stringify({
            email: userFormData.email,
            password: userFormData.password || undefined
          })
        });

        const promoteData = await promoteResponse.json();
        if (promoteResponse.ok && promoteData.status === 'success') {
          setSuccess('Superadmin created successfully');
          setShowAddUserForm(false);
          setUserFormData({
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            role: 'user',
            companyId: isSuperAdmin && selectedTenant ? selectedTenant : ''
          });

          // Reload users; for superadmin, this may be cross-tenant or system users.
          if (isSuperAdmin && selectedTenant) {
            loadUsersForTenant(selectedTenant);
          } else if (isSuperAdmin) {
            setUsers([]);
            setError('Select a tenant tab before creating users');
          } else {
            loadUsers();
          }
          setTimeout(() => setSuccess(null), 3000);
          return;
        }

        setError(promoteData.message || 'Failed to create superadmin user');
        return;
      }

      const endpoint = isSuperAdmin
        ? `${API_BASE_URL}/api/super-admin/users`
        : `${API_BASE_URL}/api/auth/users`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(userFormData)
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('User created successfully');
        setShowAddUserForm(false);
        setUserFormData({ 
          email: '', 
          password: '', 
          firstName: '', 
          lastName: '', 
          role: 'user', 
          companyId: isSuperAdmin && selectedTenant ? selectedTenant : '' 
        });
        
        if (isSuperAdmin && selectedTenant) {
          loadUsersForTenant(selectedTenant);
        } else {
          loadUsers();
        }
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to create user');
      }
    } catch (err) {
      setError('Error creating user: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!(await confirmDialog({
      title: 'Delete this user?',
      message: `${userEmail} will lose access to NovaBotX. Documents they uploaded are kept.`,
      confirmLabel: 'Delete user',
      tone: 'danger',
    }))) {
      return;
    }

    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('User deleted successfully');
        if (isSuperAdmin && selectedTenant) {
          loadUsersForTenant(selectedTenant);
        } else {
          loadUsers();
        }
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to delete user');
      }
    } catch (err) {
      setError('Error deleting user: ' + err.message);
    }
  };

  const handleUpdateRole = async (userId, newRole, oldRole, userEmail) => {
    // If promoting to superadmin, show confirmation
    if (oldRole !== 'superadmin' && newRole === 'superadmin') {
      if (!(await confirmDialog({
        title: 'Promote to super admin?',
        message: `${userEmail} will become a super admin. This will:`,
        details: [
          'Move the user to the system database',
          'Remove their tenant association',
          'Grant them access to all tenants',
        ],
        note: 'They need to sign out and back in for this to take effect.',
        confirmLabel: 'Promote',
      }))) {
        return;
      }
    }
    
    // If demoting from superadmin, show modal to select target tenant
    if (oldRole === 'superadmin' && newRole !== 'superadmin') {
      setDemoteUserData({ userId, userEmail, newRole });
      setShowDemoteModal(true);
      return; // Don't proceed until modal is confirmed
    }
    
    // Regular role update
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/users/${userId}/role`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ role: newRole })
      });

      const data = await response.json();
      if (data.status === 'success') {
        const message = newRole === 'superadmin' 
          ? `User "${userEmail}" promoted to superadmin successfully. They will need to log out and log back in.`
          : 'User role updated successfully';
        setSuccess(message);
        if (isSuperAdmin) {
          if (selectedTenant === 'system') {
            loadSystemUsers();
          } else if (selectedTenant) {
            loadUsersForTenant(selectedTenant);
          } else {
            setUsers([]);
          }
          loadTenants(); // Refresh tenant list
        } else {
          loadUsers();
        }
        setTimeout(() => setSuccess(null), newRole === 'superadmin' ? 5000 : 3000);
      } else {
        setError(data.message || 'Failed to update role');
      }
    } catch (err) {
      setError('Error updating role: ' + err.message);
    }
  };

  const loadTenantStats = async (companyId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/tenants/${companyId}/stats`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success') {
        setTenantStats(data.data);
      }
    } catch (err) {
      console.error('Error loading tenant stats:', err);
    }
  };


  const handleSuspendTenant = async (companyId) => {
    if (!(await confirmDialog({
      title: 'Suspend this tenant?',
      message: 'Its users won\u2019t be able to access NovaBotX until the tenant is reactivated.',
      confirmLabel: 'Suspend tenant',
      tone: 'danger',
    }))) {
      return;
    }

    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${companyId}/suspend`, {
        method: 'POST',
        headers: authService.getAuthHeaders()
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('Tenant suspended successfully');
        loadTenants();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to suspend tenant');
      }
    } catch (err) {
      setError('Error suspending tenant: ' + err.message);
    }
  };

  const handleActivateTenant = async (companyId) => {
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${companyId}/activate`, {
        method: 'POST',
        headers: authService.getAuthHeaders()
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('Tenant activated successfully');
        loadTenants();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to activate tenant');
      }
    } catch (err) {
      setError('Error activating tenant: ' + err.message);
    }
  };

  const handleDeleteTenant = async (companyId, hardDelete = false) => {
    const tenant = tenants.find(t => t.companyId === companyId);
    const tenantName = tenant ? tenant.companyName : companyId;
    
    const confirmed = await confirmDialog(hardDelete
      ? {
        title: `Permanently delete “${tenantName}”?`,
        message: 'This removes the tenant record.',
        details: ['Its database and Qdrant collection still exist and must be deleted manually'],
        note: 'This cannot be undone.',
        confirmLabel: 'Delete permanently',
        tone: 'danger',
      }
      : {
        title: `Delete tenant “${tenantName}”?`,
        message: 'This suspends the tenant, so its users can\u2019t access NovaBotX. Use hard delete to remove it permanently.',
        confirmLabel: 'Delete tenant',
        tone: 'danger',
      });

    if (!confirmed) {
      return;
    }

    setError(null);
    try {
      const url = `${API_BASE_URL}/api/tenant/${companyId}${hardDelete ? '?hardDelete=true' : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess(data.message || 'Tenant deleted successfully');
        loadTenants();
        // Reset selected tenant if it was deleted
        if (selectedTenant === companyId) {
          setSelectedTenant('system');
        }
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.message || 'Failed to delete tenant');
      }
    } catch (err) {
      setError('Error deleting tenant: ' + err.message);
    }
  };

  const handleEditWelcomeMessage = async (companyId) => {
    setError(null);
    setSuccess(null);
    try {
      // Fetch current welcome message from API to ensure we have the latest
      const response = await fetch(`${API_BASE_URL}/api/tenant/${companyId}`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.status === 'success' && data.data?.tenant) {
        const currentMessage = data.data.tenant.settings?.welcomeMessage || 
          "Hi! I'm NovaBot, your AI Assistant. I can help you with company policies, benefits, PTO requests, and more. What can I help you with today?";
        setWelcomeMessage(currentMessage);
        setEditingWelcomeMessageFor(companyId);
        setShowWelcomeMessageEditor(true);
      } else {
        // Fallback to tenant from list
        const tenant = tenants.find(t => t.companyId === companyId);
        const currentMessage = tenant?.settings?.welcomeMessage || 
          "Hi! I'm NovaBot, your AI Assistant. I can help you with company policies, benefits, PTO requests, and more. What can I help you with today?";
        setWelcomeMessage(currentMessage);
        setEditingWelcomeMessageFor(companyId);
        setShowWelcomeMessageEditor(true);
      }
    } catch (err) {
      console.error('Error loading welcome message:', err);
      // Fallback to tenant from list
      const tenant = tenants.find(t => t.companyId === companyId);
      const currentMessage = tenant?.settings?.welcomeMessage || 
        "Hi! I'm NovaBot, your AI Assistant. I can help you with company policies, benefits, PTO requests, and more. What can I help you with today?";
      setWelcomeMessage(currentMessage);
      setEditingWelcomeMessageFor(companyId);
      setShowWelcomeMessageEditor(true);
    }
  };

  const handleSaveWelcomeMessage = async () => {
    if (!editingWelcomeMessageFor) return;
    
    if (!welcomeMessage.trim()) {
      setError('Welcome message cannot be empty');
      return;
    }

    if (welcomeMessage.length > 1000) {
      setError('Welcome message cannot exceed 1000 characters');
      return;
    }

    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${editingWelcomeMessageFor}/welcome-message`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ welcomeMessage: welcomeMessage.trim() })
      });

      const data = await response.json();
      console.log('Save welcome message response:', data);
      if (data.status === 'success') {
        setSuccess('Welcome message updated successfully');
        setShowWelcomeMessageEditor(false);
        setEditingWelcomeMessageFor(null);
        setWelcomeMessage('');
        // Reload tenants to get updated welcome message
        if (isSuperAdmin) {
          loadTenants();
        }
        // Signal to chat page that welcome message was updated
        localStorage.setItem('welcome-message-updated', Date.now().toString());
        // Trigger a custom event for same-tab updates
        window.dispatchEvent(new Event('welcome-message-updated'));
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to update welcome message');
      }
    } catch (err) {
      setError('Error updating welcome message: ' + err.message);
    }
  };

  const handleEditContactEmails = async (companyId) => {
    setError(null);
    setSuccess(null);
    try {
      // Fetch current contact emails from API to ensure we have the latest
      const response = await fetch(`${API_BASE_URL}/api/tenant/${companyId}/contact-emails`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.status === 'success' && data.data) {
        setHrContactEmail(data.data.hrContactEmail || '');
        setFinanceContactEmail(data.data.financeContactEmail || '');
        setEditingContactEmailsFor(companyId);
        setShowContactEmailsEditor(true);
      } else {
        // Fallback to tenant from list
        const tenant = tenants.find(t => t.companyId === companyId);
        setHrContactEmail(tenant?.settings?.hrContactEmail || '');
        setFinanceContactEmail(tenant?.settings?.financeContactEmail || '');
        setEditingContactEmailsFor(companyId);
        setShowContactEmailsEditor(true);
      }
    } catch (err) {
      console.error('Error loading contact emails:', err);
      // Fallback to tenant from list
      const tenant = tenants.find(t => t.companyId === companyId);
      setHrContactEmail(tenant?.settings?.hrContactEmail || '');
      setFinanceContactEmail(tenant?.settings?.financeContactEmail || '');
      setEditingContactEmailsFor(companyId);
      setShowContactEmailsEditor(true);
    }
  };

  const handleSaveContactEmails = async () => {
    if (!editingContactEmailsFor) return;
    
    // Validate email format if provided
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (hrContactEmail && !emailRegex.test(hrContactEmail.trim())) {
      setError('HR contact email must be a valid email address');
      return;
    }
    
    if (financeContactEmail && !emailRegex.test(financeContactEmail.trim())) {
      setError('Finance contact email must be a valid email address');
      return;
    }

    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${editingContactEmailsFor}/contact-emails`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ 
          hrContactEmail: hrContactEmail.trim() || null,
          financeContactEmail: financeContactEmail.trim() || null
        })
      });

      const data = await response.json();
      console.log('Save contact emails response:', data);
      if (data.status === 'success') {
        setSuccess('Contact emails updated successfully');
        setShowContactEmailsEditor(false);
        setEditingContactEmailsFor(null);
        setHrContactEmail('');
        setFinanceContactEmail('');
        // Reload tenants to get updated contact emails
        if (isSuperAdmin) {
          loadTenants();
        }
        // Signal to chat pages that contact emails were updated
        localStorage.setItem('contact-emails-updated', Date.now().toString());
        // Trigger a custom event for same-tab updates
        window.dispatchEvent(new Event('contact-emails-updated'));
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to update contact emails');
      }
    } catch (err) {
      setError('Error updating contact emails: ' + err.message);
    }
  };

  const handleEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setTenantFormData({
      companyId: tenant.companyId,
      companyName: tenant.companyName,
      domain: tenant.domain,
      adminEmail: tenant.adminEmail || '',
      adminPassword: '',
      adminFirstName: '',
      adminLastName: ''
    });
    setShowEditTenantForm(true);
  };

  const handleConfirmDemote = async (targetCompanyId) => {
    if (!demoteUserData) return;
    
    const { userId, userEmail, newRole } = demoteUserData;
    
    // Verify tenant exists
    const tenant = tenants.find(t => t.companyId === targetCompanyId);
    if (!tenant) {
      setError(`Tenant "${targetCompanyId}" not found. Please select a valid tenant.`);
      return;
    }
    
    setError(null);
    setShowDemoteModal(false);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/users/${userId}/role`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ role: newRole, targetCompanyId: targetCompanyId })
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess(`User "${userEmail}" demoted from superadmin successfully. They will need to log out and log back in.`);
        if (isSuperAdmin) {
          if (selectedTenant === 'system') {
            loadSystemUsers();
          } else if (selectedTenant) {
            loadUsersForTenant(selectedTenant);
          } else {
            setUsers([]);
          }
          loadTenants(); // Refresh tenant list
        } else {
          loadUsers();
        }
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.message || 'Failed to demote user from superadmin');
      }
    } catch (err) {
      setError('Error demoting user: ' + err.message);
    }
    
    setDemoteUserData(null);
    setDemoteTargetTenant('');
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/${editingTenant.companyId}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          companyName: tenantFormData.companyName,
          settings: editingTenant.settings // Keep existing settings for now
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('Tenant updated successfully');
        setShowEditTenantForm(false);
        setEditingTenant(null);
        loadTenants();
        if (selectedTenant === editingTenant.companyId) {
          loadTenantStats(selectedTenant);
        }
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to update tenant');
      }
    } catch (err) {
      setError('Error updating tenant: ' + err.message);
    }
  };

  const handleDeleteDocument = async (filename, docCompanyId) => {
    if (!(await confirmDialog({
      title: 'Delete this document?',
      message: `“${filename}” will be removed from NovaBotX\u2019s knowledge and its storage freed.`,
      confirmLabel: 'Delete document',
      tone: 'danger',
    }))) {
      return;
    }

    setError(null);
    try {
      // Super admin needs to pass companyId, regular admin doesn't need to (uses their own)
      const body = isSuperAdmin && (docCompanyId || selectedTenant)
        ? { filename, companyId: docCompanyId || selectedTenant }
        : { filename };

      const response = await fetch(`${API_BASE_URL}/api/knowledge/delete`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Document deleted successfully');
        if (isSuperAdmin && selectedTenant) {
          loadDocumentsForTenant(selectedTenant);
          loadTenantStats(selectedTenant); // Refresh stats after deletion
        } else {
          loadDocuments();
          if (isAdmin && !isSuperAdmin) {
            loadAdminTenantStats(); // Refresh stats for regular admin
          }
        }
        loadUploadUsage(); // Refresh upload usage after deletion
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to delete document');
      }
    } catch (err) {
      setError('Error deleting document: ' + err.message);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const id = doc?.multerFilename;
      if (!id) {
        alert('Download is unavailable for this document.');
        return;
      }
      const response = await fetch(`${API_BASE_URL}/api/knowledge/download/${encodeURIComponent(id)}`, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        let msg = `Download failed (${response.status}).`;
        try {
          const body = await response.json();
          msg = body?.message || msg;
        } catch {
          /* ignore */
        }
        alert(msg);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc?.filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Download error: ${err.message}`);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getSelectedTenantName = () => {
    if (!selectedTenant) return '';
    if (selectedTenant === 'system') return 'System Database';
    const tenant = tenants.find(t => t.companyId === selectedTenant);
    return tenant ? tenant.companyName : selectedTenant;
  };

  const getUserInitials = (user) => {
    if (!user?.email) return '?';
    const local = user.email.split('@')[0];
    const parts = local.split(/[._-]+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return local.slice(0, 2).toUpperCase();
  };

  const getUserDisplayName = (user) => {
    if (!user?.email) return 'User';
    return user.email.split('@')[0];
  };

  // Greeting name: profile first name, else "yashwanth.setty@…" → "Yashwanth" (same rule as the chat page).
  const getUserFirstName = (user) => {
    const raw = user?.firstName || user?.email?.split('@')[0].split(/[._-]+/)[0] || '';
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'there';
  };

  const formatRoleLabel = (role) => {
    if (!role) return 'User';
    if (role === 'superadmin') return 'Super Admin';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const tabTitles = {
    overview: 'Overview',
    users: 'User management',
    documents: 'Document management',
    feedback: 'User feedback',
    metrics: 'Metrics dashboard',
    logo: 'Logo settings'
  };

  const renderNavIcon = (name) => {
    const icons = {
      overview: <FiGrid />,
      users: <FiUsers />,
      documents: <FiFileText />,
      feedback: <FiMessageSquare />,
      metrics: <FiBarChart2 />,
      logo: <FiImage />
    };
    return icons[name] || null;
  };

  const renderOverviewUploadUsage = () => {
    if (!uploadUsage) return null;
    return (
      <div className="admin-overview-card admin-overview-card-wide">
        <div className="admin-overview-card-header">
          <h3><span className="admin-card-icon" aria-hidden="true"><FiHardDrive /></span>Upload usage</h3>
          <span className={`admin-usage-remaining ${uploadUsage.usagePercentage > 90 ? 'danger' : uploadUsage.usagePercentage > 75 ? 'warn' : ''}`}>
            {uploadUsage.remainingMB} MB remaining
          </span>
        </div>
        <div className="admin-usage-bar-track">
          <div
            className={`admin-usage-bar-fill ${uploadUsage.usagePercentage > 90 ? 'danger' : uploadUsage.usagePercentage > 75 ? 'warn' : ''}`}
            style={{ width: `${Math.min(uploadUsage.usagePercentage, 100)}%` }}
          />
        </div>
        <p className="admin-usage-caption">
          {uploadUsage.currentMB} MB / {uploadUsage.limitMB} MB used
        </p>
      </div>
    );
  };

  const handleViewTenantDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/qdrant-documents`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        const message = `Qdrant Collection: ${data.collection.name}\n`
          + `Total Vectors: ${data.collection.totalPoints}\n`
          + `Documents Found: ${data.summary.documentCount}\n`
          + `Vectors from Documents: ${data.summary.totalVectorsFromDocuments}\n`
          + `Orphaned Vectors: ${data.summary.orphanedVectors} (${data.summary.orphanedPercentage}%)\n\n`
          + `Documents:\n${data.documents.map((d, i) => `${i + 1}. ${d.source}: ${d.vectorCount} vectors`).join('\n')}`;
        alert(message);
      }
    } catch (err) {
      console.error('Failed to get Qdrant documents:', err);
      alert('Failed to get Qdrant document details');
    }
  };


  const showLogoTab = isAdmin && !isSuperAdmin;
  const showTenantTabs = isSuperAdmin && (activeTab === 'overview' || activeTab === 'users' || activeTab === 'documents');
  const tourReady = Boolean((isAdmin || isSuperAdmin) && currentUser);
  // Steps are frozen when the tour opens so they don't shift mid-tour.
  const tourSteps = useMemo(() => (
    touring && tourReady
      ? buildAdminTourSteps({
          isMobile: typeof window !== 'undefined' && window.innerWidth <= 900,
          showLogo: showLogoTab,
          showTenants: showTenantTabs
        })
      : []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [touring, tourReady]);

  return (
    <div className="admin-dashboard">
      <div className="admin-shell">
        <aside className="admin-sidebar">
          <div className="admin-sidebar-top">
            <div className="admin-brand">
              <span className="admin-brand-mark" aria-hidden="true"><FiShield /></span>
              <div className="admin-brand-text">
                <span className="admin-sidebar-kicker">NovaBotX</span>
                <h1 className="admin-sidebar-title">Admin console</h1>
              </div>
            </div>
            <button type="button" className="admin-back-link" onClick={() => navigate('/')} aria-label="Back to chat">
              <FiArrowLeft aria-hidden="true" /> <span>Back to chat</span>
            </button>
          </div>

          <div className="admin-sidebar-section-label">Workspace</div>
          <nav className="admin-sidebar-nav" aria-label="Admin navigation" data-tour="admin-nav">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'users', label: 'User management' },
              { id: 'documents', label: 'Document management' },
              { id: 'feedback', label: 'User feedback' },
              { id: 'metrics', label: 'Metrics dashboard' }
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                className={`admin-sidebar-nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => setActiveTab(item.id)}
                aria-current={activeTab === item.id ? 'page' : undefined}
                data-tour={`admin-nav-${item.id}`}
              >
                <span className="admin-sidebar-nav-icon" aria-hidden="true">{renderNavIcon(item.id)}</span>
                <span className="admin-sidebar-nav-label">{item.label}</span>
                {isSuperAdmin && selectedTenant && (item.id === 'users' || item.id === 'documents') && (
                  <span className="admin-nav-tenant-badge">{getSelectedTenantName()}</span>
                )}
              </button>
            ))}
            {showLogoTab && (
              <button
                type="button"
                className={`admin-sidebar-nav-item ${activeTab === 'logo' ? 'active' : ''}`}
                onClick={() => setActiveTab('logo')}
                aria-current={activeTab === 'logo' ? 'page' : undefined}
                data-tour="admin-nav-logo"
              >
                <span className="admin-sidebar-nav-icon" aria-hidden="true">{renderNavIcon('logo')}</span>
                <span className="admin-sidebar-nav-label">Logo settings</span>
              </button>
            )}
          </nav>

          <div className="admin-sidebar-spacer" />

          <div className="admin-sidebar-user">
            <div className="admin-sidebar-avatar">{getUserInitials(currentUser)}</div>
            <div className="admin-sidebar-user-meta">
              <div className="admin-sidebar-user-name">{getUserDisplayName(currentUser)}</div>
              <div className="admin-sidebar-user-role">{formatRoleLabel(currentUser?.role)}</div>
            </div>
            <button type="button" className="admin-sidebar-menu-btn" onClick={handleLogout} aria-label="Logout" title="Logout">
              <FiLogOut aria-hidden="true" />
            </button>
          </div>
        </aside>

        <div className="admin-main">
          <header className="admin-main-header">
            <div className="admin-main-heading">
              <span className="admin-kicker">Admin{isSuperAdmin ? ' · Super admin' : ''}</span>
              <h2 className="admin-main-title">{tabTitles[activeTab] || 'Admin'}</h2>
            </div>
            <div className="admin-main-header-actions" data-tour="admin-header-actions">
              <HelpMenu onTour={startTour} />
              <FeedbackButton />
              <TourButton onClick={startTour} />
              <ThemeToggle />
              <SubscriptionMenuButton
                onSelect={(id) => {
                  setSubscriptionCenterTab(id || 'billing');
                  setShowSubscriptionCenter(true);
                }}
              />
              <button type="button" className="admin-chat-btn" onClick={() => navigate('/')} title="Go to Chat" aria-label="Go to chat">
                <FiMessageCircle aria-hidden="true" /> <span>Chat</span>
              </button>
            </div>
          </header>

          {currentUser?.trial?.isTrialUser && (
            <div className="admin-trial-wrap">
              <TrialBanner trial={currentUser.trial} compact />
            </div>
          )}

          <div className="admin-dashboard-scroll">
      {/* Super Admin Tenant Tabs */}
      {showTenantTabs && (
        <div className="admin-tenant-tabs" data-tour="admin-tenants">
          <div className="admin-tenant-tabs-header">
            <h3><span className="admin-card-icon" aria-hidden="true"><FiLayers /></span>Tenants</h3>
            <button
              className="admin-add-btn"
              onClick={() => setShowCreateTenantForm(true)}
            >
              <FiPlus aria-hidden="true" /> Create new tenant
            </button>
          </div>
          <div className="admin-tenant-tabs-list">
            {/* System Database Tab */}
            <div key="system" className="admin-tenant-tab-wrapper">
              <button
                className={`admin-tenant-tab ${selectedTenant === 'system' ? 'active' : ''}`}
                onClick={() => setSelectedTenant('system')}
              >
                <FiDatabase aria-hidden="true" /> System Database
              </button>
            </div>
            {tenants.map((tenant) => (
              <div key={tenant.companyId} className="admin-tenant-tab-wrapper">
                <button
                  className={`admin-tenant-tab ${selectedTenant === tenant.companyId ? 'active' : ''}`}
                  onClick={() => setSelectedTenant(tenant.companyId)}
                >
                  {tenant.companyName} ({tenant.companyId})
                  {tenant.status !== 'active' && (
                    <span className="tenant-status-badge">{tenant.status}</span>
                  )}
                </button>
              </div>
            ))}
          </div>
          {/* Tenant Statistics and Actions */}
          {selectedTenant && selectedTenant !== 'system' && tenantStats && (() => {
            const currentTenant = tenants.find(t => t.companyId === selectedTenant);
            return currentTenant ? (
              <div className="admin-tenant-stats">
                <div className="admin-tenant-stats-header">
                  <h4>Tenant Statistics</h4>
                  <div className="admin-tenant-actions">
                    <button
                      className="admin-tenant-action-btn edit"
                      onClick={() => handleEditTenant(currentTenant)}
                      title="Edit Tenant"
                    >
                      <FiEdit2 aria-hidden="true" /> Edit
                    </button>
                    <button
                      className="admin-tenant-action-btn welcome"
                      onClick={() => handleEditWelcomeMessage(selectedTenant)}
                      title="Edit Welcome Message"
                    >
                      <FiMessageSquare aria-hidden="true" /> Welcome message
                    </button>
                    <button
                      className="admin-tenant-action-btn contact"
                      onClick={() => handleEditContactEmails(selectedTenant)}
                      title="Edit Contact Emails"
                    >
                      <FiMail aria-hidden="true" /> Contact emails
                    </button>
                    {currentTenant.status === 'active' ? (
                      <button
                        className="admin-tenant-action-btn suspend"
                        onClick={() => handleSuspendTenant(selectedTenant)}
                        title="Suspend Tenant"
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        className="admin-tenant-action-btn activate"
                        onClick={() => handleActivateTenant(selectedTenant)}
                        title="Activate Tenant"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      className="admin-tenant-action-btn delete"
                      onClick={() => handleDeleteTenant(selectedTenant, false)}
                      title="Delete Tenant (Soft Delete - Suspends)"
                    >
                      <FiTrash2 aria-hidden="true" /> Delete
                    </button>
                    <button
                      className="admin-tenant-action-btn delete-hard"
                      onClick={() => handleDeleteTenant(selectedTenant, true)}
                      title="Hard Delete Tenant (Permanent)"
                    >
                      <FiAlertTriangle aria-hidden="true" /> Hard delete
                    </button>
                  </div>
                </div>
              <div className="admin-stats-grid">
                <div className="admin-stat-item">
                  <span className="admin-stat-icon" aria-hidden="true"><FiUsers /></span>
                  <span className="stat-label">Users</span>
                  <span className="stat-value"><AnimatedNumber value={tenantStats.stats?.userCount || 0} /></span>
                </div>
                <div className="admin-stat-item">
                  <span className="admin-stat-icon tone-green" aria-hidden="true"><FiFileText /></span>
                  <span className="stat-label">Documents</span>
                  <span className="stat-value"><AnimatedNumber value={tenantStats.stats?.documentCount || 0} /></span>
                </div>
                <div className="admin-stat-item">
                  <span className="admin-stat-icon tone-teal" aria-hidden="true"><FiCpu /></span>
                  <span className="stat-label">Vectors</span>
                  <span className="stat-value"><AnimatedNumber value={tenantStats.stats?.vectorCount || 0} /></span>
                </div>
                <div className="admin-stat-item">
                  <span className="admin-stat-icon tone-ink" aria-hidden="true"><FiZap /></span>
                  <span className="stat-label">Status</span>
                  <span className={`stat-value status ${currentTenant.status || 'unknown'}`}>
                    {currentTenant.status || 'unknown'}
                  </span>
                </div>
              </div>
            </div>
            ) : null;
          })()}
          {/* System Database Info */}
          {selectedTenant === 'system' && (
            <div className="admin-tenant-stats">
              <h4>System Database</h4>
              <div className="admin-stats-grid">
                <div className="admin-stat-item">
                  <span className="admin-stat-icon" aria-hidden="true"><FiShield /></span>
                  <span className="stat-label">Superadmins</span>
                  <span className="stat-value"><AnimatedNumber value={users.filter(u => u.role === 'superadmin').length} /></span>
                </div>
                <div className="admin-stat-item">
                  <span className="admin-stat-icon tone-green" aria-hidden="true"><FiUsers /></span>
                  <span className="stat-label">Total Users</span>
                  <span className="stat-value"><AnimatedNumber value={users.length} /></span>
                </div>
                <div className="admin-stat-item">
                  <span className="admin-stat-icon tone-teal" aria-hidden="true"><FiServer /></span>
                  <span className="stat-label">Database</span>
                  <span className="stat-value is-small">novabot</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Tenant Form (Super Admin Only) */}
      {isSuperAdmin && showCreateTenantForm && (
        <div className="admin-form-overlay">
          <div className="admin-form-container">
            <form className="admin-form" onSubmit={handleCreateTenant}>
              <h3>Create New Tenant</h3>
              <div className="admin-form-group">
                <label>Company ID *</label>
                <input
                  type="text"
                  required
                  value={tenantFormData.companyId}
                  onChange={(e) => setTenantFormData({ ...tenantFormData, companyId: e.target.value })}
                  placeholder="e.g., novalink-solutions"
                />
                <small>Used for database name (lowercase, alphanumeric, hyphens, dots, underscores)</small>
              </div>
              <div className="admin-form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  required
                  value={tenantFormData.companyName}
                  onChange={(e) => setTenantFormData({ ...tenantFormData, companyName: e.target.value })}
                  placeholder="e.g., NovaLink Solutions"
                />
              </div>
              <div className="admin-form-group">
                <label>Domain *</label>
                <input
                  type="text"
                  required
                  value={tenantFormData.domain}
                  onChange={(e) => setTenantFormData({ ...tenantFormData, domain: e.target.value })}
                  placeholder="e.g., novalink-solutions.com"
                />
                <small>Email domain for this tenant</small>
              </div>
              <div className="admin-form-group">
                <label>Admin Email *</label>
                <input
                  type="email"
                  required
                  value={tenantFormData.adminEmail}
                  onChange={(e) => setTenantFormData({ ...tenantFormData, adminEmail: e.target.value })}
                  placeholder="admin@example.com"
                />
              </div>
              <div className="admin-form-group">
                <label>Admin Password *</label>
                <input
                  type="password"
                  required
                  value={tenantFormData.adminPassword}
                  onChange={(e) => setTenantFormData({ ...tenantFormData, adminPassword: e.target.value })}
                />
              </div>
              <div className="admin-form-group">
                <label>Admin First Name</label>
                <input
                  type="text"
                  value={tenantFormData.adminFirstName}
                  onChange={(e) => setTenantFormData({ ...tenantFormData, adminFirstName: e.target.value })}
                />
              </div>
              <div className="admin-form-group">
                <label>Admin Last Name</label>
                <input
                  type="text"
                  value={tenantFormData.adminLastName}
                  onChange={(e) => setTenantFormData({ ...tenantFormData, adminLastName: e.target.value })}
                />
              </div>
              <div className="admin-form-actions">
                <button type="submit" className="admin-submit-btn">
                  Create Tenant
                </button>
                <button
                  type="button"
                  className="admin-cancel-btn"
                  onClick={() => {
                    setShowCreateTenantForm(false);
                    setTenantFormData({
                      companyId: '',
                      companyName: '',
                      domain: '',
                      adminEmail: '',
                      adminPassword: '',
                      adminFirstName: '',
                      adminLastName: ''
                    });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tenant Form (Super Admin Only) */}
      {isSuperAdmin && showEditTenantForm && editingTenant && (
        <div className="admin-form-overlay">
          <div className="admin-form-container">
            <form className="admin-form" onSubmit={handleUpdateTenant}>
              <h3>Edit Tenant</h3>
              <div className="admin-form-group">
                <label>Company ID</label>
                <input
                  type="text"
                  value={tenantFormData.companyId}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                <small>Company ID cannot be changed</small>
              </div>
              <div className="admin-form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  required
                  value={tenantFormData.companyName}
                  onChange={(e) => setTenantFormData({ ...tenantFormData, companyName: e.target.value })}
                  placeholder="e.g., NovaLink Solutions"
                />
              </div>
              <div className="admin-form-group">
                <label>Domain</label>
                <input
                  type="text"
                  value={tenantFormData.domain}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                <small>Domain cannot be changed</small>
              </div>
              <div className="admin-form-actions">
                <button type="submit" className="admin-submit-btn">
                  Update Tenant
                </button>
                <button
                  type="button"
                  className="admin-cancel-btn"
                  onClick={() => {
                    setShowEditTenantForm(false);
                    setEditingTenant(null);
                    setTenantFormData({
                      companyId: '',
                      companyName: '',
                      domain: '',
                      adminEmail: '',
                      adminPassword: '',
                      adminFirstName: '',
                      adminLastName: ''
                    });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Welcome Message Editor Modal */}
      {showWelcomeMessageEditor && editingWelcomeMessageFor && (() => {
        const currentTenant = tenants.find(t => t.companyId === editingWelcomeMessageFor);
        const tenantName = currentTenant ? currentTenant.companyName : editingWelcomeMessageFor;
        return (
          <div className="admin-form-overlay">
            <div className="admin-form-container">
              <div className="admin-form">
                <div className="admin-form-header">
                  <h3>Edit Welcome Message</h3>
                  <p>
                    {isSuperAdmin ? (
                      <>Customize the welcome message for <strong>{tenantName}</strong></>
                    ) : (
                      <>Customize the welcome message for your tenant</>
                    )}
                  </p>
                </div>
                <div className="admin-form-body">
                  <div className="admin-form-group">
                    <label htmlFor="welcome-message-textarea">Welcome Message</label>
                    <textarea
                      id="welcome-message-textarea"
                      value={welcomeMessage}
                      onChange={(e) => setWelcomeMessage(e.target.value)}
                      placeholder="Enter the welcome message that users will see when they start a new chat..."
                      rows={8}
                      maxLength={1000}
                    />
                    <div className="admin-form-meta">
                      <small>
                        This message will be displayed to users when they start a new chat session.
                      </small>
                      <small className={`admin-char-count ${welcomeMessage.length > 900 ? 'danger' : welcomeMessage.length > 800 ? 'warn' : ''}`}>
                        {welcomeMessage.length} / 1000 characters
                      </small>
                    </div>
                  </div>
                  {error && (
                    <div className="admin-message error">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="admin-message success">
                      {success}
                    </div>
                  )}
                </div>
                <div className="admin-form-actions">
                  <button
                    type="button"
                    className="admin-submit-btn"
                    onClick={handleSaveWelcomeMessage}
                  >
                    Save Welcome Message
                  </button>
                  <button
                    type="button"
                    className="admin-cancel-btn"
                    onClick={() => {
                      setShowWelcomeMessageEditor(false);
                      setEditingWelcomeMessageFor(null);
                      setWelcomeMessage('');
                      setError(null);
                      setSuccess(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Contact Emails Editor Modal */}
      {showContactEmailsEditor && editingContactEmailsFor && (() => {
        const tenant = tenants.find(t => t.companyId === editingContactEmailsFor);
        const tenantName = tenant ? tenant.companyName : editingContactEmailsFor;
        return (
          <div className="admin-form-overlay">
            <div className="admin-form-container">
              <div className="admin-form">
                <div className="admin-form-header">
                  <h3>Edit Contact Emails</h3>
                  <p>
                    {isSuperAdmin ? (
                      <>Configure contact emails for <strong>{tenantName}</strong></>
                    ) : (
                      <>Configure contact emails for your tenant</>
                    )}
                  </p>
                </div>
                <div className="admin-form-body">
                  <div className="admin-form-group">
                    <label htmlFor="hr-contact-email">HR Contact Email</label>
                    <input
                      id="hr-contact-email"
                      type="email"
                      value={hrContactEmail}
                      onChange={(e) => setHrContactEmail(e.target.value)}
                      placeholder="hr@yourcompany.com"
                    />
                    <small>
                      Email address shown to users for HR-related questions when NovaBot cannot answer.
                      Leave empty to show a generic message directing users to contact their HR department.
                    </small>
                  </div>
                  <div className="admin-form-group">
                    <label htmlFor="finance-contact-email">Finance Contact Email</label>
                    <input
                      id="finance-contact-email"
                      type="email"
                      value={financeContactEmail}
                      onChange={(e) => setFinanceContactEmail(e.target.value)}
                      placeholder="finance@yourcompany.com"
                    />
                    <small>
                      Email address shown to users for Finance-related questions when NovaBot cannot answer.
                      Leave empty to show a generic message directing users to contact their Finance department.
                    </small>
                  </div>
                  {error && (
                    <div className="admin-message error">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="admin-message success">
                      {success}
                    </div>
                  )}
                </div>
                <div className="admin-form-actions">
                  <button
                    type="button"
                    className="admin-submit-btn"
                    onClick={handleSaveContactEmails}
                  >
                    Save Contact Emails
                  </button>
                  <button
                    type="button"
                    className="admin-cancel-btn"
                    onClick={() => {
                      setShowContactEmailsEditor(false);
                      setEditingContactEmailsFor(null);
                      setHrContactEmail('');
                      setFinanceContactEmail('');
                      setError(null);
                      setSuccess(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Demote Superadmin Modal */}
      {isSuperAdmin && showDemoteModal && demoteUserData && (
        <div className="admin-form-overlay">
          <div className="admin-form-container">
            <div className="admin-form">
              <h3>Demote Superadmin</h3>
              <p className="admin-form-note">
                To demote <strong>{demoteUserData.userEmail}</strong> from superadmin, select the target tenant where the user will be moved.
              </p>
              <div className="admin-form-group">
                <label>Target Tenant (Company ID) *</label>
                <select
                  value={demoteTargetTenant}
                  onChange={(e) => setDemoteTargetTenant(e.target.value)}
                  required
                >
                  <option value="">Select a tenant</option>
                  {tenants.map(tenant => (
                    <option key={tenant.companyId} value={tenant.companyId}>
                      {tenant.companyName} ({tenant.companyId})
                    </option>
                  ))}
                </select>
                <small>Tenant where the user will be moved</small>
              </div>
              <div className="admin-form-group">
                <label>Target Role</label>
                <input
                  type="text"
                  value={demoteUserData.newRole}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                <small>Role will be set to: {demoteUserData.newRole}</small>
              </div>
              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-submit-btn"
                  onClick={() => {
                    if (!demoteTargetTenant) {
                      setError('Please select a target tenant');
                      return;
                    }
                    handleConfirmDemote(demoteTargetTenant);
                  }}
                >
                  Demote User
                </button>
                <button
                  type="button"
                  className="admin-cancel-btn"
                  onClick={() => {
                    setShowDemoteModal(false);
                    setDemoteUserData(null);
                    setDemoteTargetTenant('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="admin-content" data-tour="admin-content">
        {error && (
          <div className="admin-message error" role="alert">
            {error}
          </div>
        )}

        {success && (
          <div className="admin-message success" role="status">
            {success}
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="admin-overview">
            <section className="admin-hero">
              <div className="admin-hero-text">
                <span className="admin-kicker">Overview</span>
                <h3 className="admin-hero-title">
                  Welcome back, <span>{getUserFirstName(currentUser)}</span>
                </h3>
                <p className="admin-hero-desc">
                  Keep NovaBotX sharp: add trusted knowledge, manage who has access, and watch how your team uses it.
                </p>
              </div>
              <div className="admin-hero-actions">
                <button type="button" className="admin-add-btn" onClick={() => setActiveTab('documents')}>
                  <FiUploadCloud aria-hidden="true" /> Add knowledge
                </button>
                <button type="button" className="admin-ghost-btn" onClick={() => setActiveTab('users')}>
                  <FiUsers aria-hidden="true" /> Manage users
                </button>
                <button type="button" className="admin-ghost-btn" onClick={() => setActiveTab('metrics')}>
                  <FiBarChart2 aria-hidden="true" /> Metrics
                </button>
              </div>
            </section>

            {(isAdmin || isSuperAdmin) && renderOverviewUploadUsage()}

            {isAdmin && !isSuperAdmin && adminTenantStats && (
              <div className="admin-overview-card admin-overview-card-wide">
                <div className="admin-overview-card-header">
                  <h3><span className="admin-card-icon tone-green" aria-hidden="true"><FiBox /></span>Tenant statistics</h3>
                  <button type="button" className="admin-overview-edit-btn" onClick={handleViewTenantDetails}>
                    View details
                  </button>
                </div>
                <div className="admin-stats-grid">
                  <div className="admin-stat-item">
                    <span className="admin-stat-icon" aria-hidden="true"><FiUsers /></span>
                    <span className="stat-label">Users</span>
                    <span className="stat-value"><AnimatedNumber value={adminTenantStats.userCount || 0} /></span>
                  </div>
                  <div className="admin-stat-item">
                    <span className="admin-stat-icon tone-green" aria-hidden="true"><FiFileText /></span>
                    <span className="stat-label">Documents</span>
                    <span className="stat-value"><AnimatedNumber value={adminTenantStats.documentCount || 0} /></span>
                  </div>
                  <div className="admin-stat-item">
                    <span className="admin-stat-icon tone-teal" aria-hidden="true"><FiCpu /></span>
                    <span className="stat-label">Vectors</span>
                    <span className="stat-value"><AnimatedNumber value={adminTenantStats.vectorCount || 0} /></span>
                  </div>
                  <div className="admin-stat-item">
                    <span className="admin-stat-icon tone-ink" aria-hidden="true"><FiDatabase /></span>
                    <span className="stat-label">Collection</span>
                    <span className="stat-value stat-value-collection" title={adminTenantStats.collectionName}>
                      {adminTenantStats.collectionName?.replace('nova_memory_', '') || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {isAdmin && !isSuperAdmin && currentUser?.companyId && (
              <div className="admin-overview-row">
                <div className="admin-overview-card admin-overview-card-half">
                  <div className="admin-overview-card-header">
                    <h3><span className="admin-card-icon" aria-hidden="true"><FiMessageSquare /></span>Welcome message</h3>
                    <button
                      type="button"
                      className="admin-overview-edit-btn"
                      onClick={() => handleEditWelcomeMessage(currentUser.companyId)}
                    >
                      Edit
                    </button>
                  </div>
                  <p className="admin-overview-desc">
                    Customize the greeting users see when they start a new chat.
                  </p>
                </div>

                <div className="admin-overview-card admin-overview-card-half">
                  <div className="admin-overview-card-header">
                    <h3><span className="admin-card-icon tone-green" aria-hidden="true"><FiMail /></span>Contact emails</h3>
                    <button
                      type="button"
                      className="admin-overview-edit-btn"
                      onClick={() => handleEditContactEmails(currentUser.companyId)}
                    >
                      Edit
                    </button>
                  </div>
                  <p className="admin-overview-desc">
                    HR and finance contacts shown when NovaBot can&apos;t answer.
                  </p>
                </div>
              </div>
            )}

            {isSuperAdmin && (
              <div className="admin-overview-card admin-overview-card-wide admin-overview-hint">
                <span className="admin-card-icon" aria-hidden="true"><FiLayers /></span>
                <p>Select a tenant tab above to manage users, documents, and tenant-specific settings.</p>
              </div>
            )}
          </div>
        )}

        {/* System Database: Documents not available */}
        {isSuperAdmin && selectedTenant === 'system' && activeTab === 'documents' && (
          <div className="admin-message info">
            Documents are stored in tenant databases. Select a tenant to view documents.
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-section admin-users-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">
                  User management
                  {isSuperAdmin && selectedTenant && (
                    <span className="section-tenant-name"> · {getSelectedTenantName()}</span>
                  )}
                  {isSuperAdmin && !selectedTenant && (
                    <span className="section-tenant-name"> · All tenants</span>
                  )}
                </h2>
                <p className="admin-section-subtitle">
                  Manage account access and roles for {users.length} {users.length === 1 ? 'user' : 'users'}.
                </p>
              </div>
              <button
                className="admin-add-btn"
                onClick={() => {
                  setShowAddUserForm(true);
                  setEditingUser(null);
                  setUserFormData({ 
                    email: '', 
                    password: '', 
                    firstName: '', 
                    lastName: '', 
                    role: 'user', 
                    companyId: isSuperAdmin && selectedTenant && selectedTenant !== 'system' ? selectedTenant : '' 
                  });
                }}
              >
<FiPlus aria-hidden="true" /> Add user
              </button>
            </div>

            {showAddUserForm && (
              <div className="admin-form-container">
                <form className="admin-form" onSubmit={handleAddUser}>
                  <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
                  {isSuperAdmin && userFormData.role !== 'superadmin' && (
                    <div className="admin-form-group">
                      <label>Tenant (Company ID) *</label>
                      <select
                        required
                        value={userFormData.companyId}
                        onChange={(e) => setUserFormData({ ...userFormData, companyId: e.target.value })}
                      >
                        <option value="">Select a tenant</option>
                        {tenants.map(tenant => (
                          <option key={tenant.companyId} value={tenant.companyId}>
                            {tenant.companyName} ({tenant.companyId})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="admin-form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      required
                      value={userFormData.email}
                      onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      required
                      value={userFormData.password}
                      onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={userFormData.firstName}
                      onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={userFormData.lastName}
                      onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                    />
                  </div>
                  <div className="admin-form-group">
                    <label>Role *</label>
                    <select
                      required
                      value={userFormData.role}
                      onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      {isSuperAdmin && <option value="superadmin">Superadmin</option>}
                    </select>
                  </div>
                  <div className="admin-form-actions">
                    <button type="submit" className="admin-submit-btn">
                      {editingUser ? 'Update User' : 'Create User'}
                    </button>
                    <button
                      type="button"
                      className="admin-cancel-btn"
                      onClick={() => {
                        setShowAddUserForm(false);
                        setEditingUser(null);
                        setUserFormData({ 
                          email: '', 
                          password: '', 
                          firstName: '', 
                          lastName: '', 
                          role: 'user', 
                          companyId: isSuperAdmin && selectedTenant ? selectedTenant : '' 
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {loading ? (
              <SkeletonRows label="Loading users..." />
            ) : (
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      {isSuperAdmin && <th>Tenant</th>}
                      <th>Role</th>
                      <th>Verified</th>
                      <th>Last login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={isSuperAdmin ? 6 : 5} className="admin-empty">No users found</td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="admin-user-identity">
                              <span className="admin-user-list-avatar">{getUserInitials(user)}</span>
                              <span className="admin-user-email">{user.email}</span>
                            </div>
                          </td>
                          {isSuperAdmin && <td>{user.tenantName || user.companyId || 'N/A'}</td>}
                          <td>
                            {isSuperAdmin ? (
                              <select
                                value={user.role || 'user'}
                                onChange={(e) => handleUpdateRole(user.id, e.target.value, user.role, user.email)}
                                className="admin-role-select"
                                disabled={user.id === currentUser?.id}
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="superadmin">Superadmin</option>
                              </select>
                            ) : (
                              <select
                                value={user.role || 'user'}
                                onChange={(e) => handleUpdateRole(user.id, e.target.value, user.role, user.email)}
                                className="admin-role-select"
                                disabled={user.id === currentUser?.id}
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                              </select>
                            )}
                          </td>
                          <td>
                            <span className={`admin-pill ${user.isEmailVerified ? 'is-ok' : 'is-warn'}`}>
                              {user.isEmailVerified ? 'Verified' : 'Not verified'}
                            </span>
                          </td>
                          <td>{user.lastLogin ? formatDate(user.lastLogin) : 'Never'}</td>
                          <td>
                            <div className="admin-action-buttons">
                              <button
                                className="admin-delete-btn"
                                onClick={() => handleDeleteUser(user.id, user.email)}
                                disabled={user.id === currentUser?.id || user.role === 'superadmin'}
                                title={
                                  user.id === currentUser?.id 
                                    ? "Cannot delete yourself" 
                                    : user.role === 'superadmin'
                                    ? "Cannot delete superadmin. Change role to demote instead."
                                    : "Delete user"
                                }
                              >
                                <FiTrash2 aria-hidden="true" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (isSuperAdmin ? (selectedTenant && selectedTenant !== 'system') : true) && (
          <div className="admin-section admin-documents-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">
                  Knowledge library
                  {isSuperAdmin && selectedTenant && selectedTenant !== 'system' && (
                    <span className="section-tenant-name"> · {getSelectedTenantName()}</span>
                  )}
                </h2>
                <p className="admin-section-subtitle">
                  Add trusted sources for NovaBotX and manage previously indexed content.
                </p>
              </div>
            </div>

            <KnowledgeUpload 
              companyId={isSuperAdmin && selectedTenant && selectedTenant !== 'system' ? selectedTenant : null}
              onUploadComplete={() => {
                if (isSuperAdmin && selectedTenant && selectedTenant !== 'system') {
                  loadDocumentsForTenant(selectedTenant);
                  loadTenantStats(selectedTenant); // Refresh stats after upload
                } else {
                  loadDocuments();
                  if (isAdmin && !isSuperAdmin) {
                    loadAdminTenantStats(); // Refresh stats for regular admin
                  }
                }
                loadUploadUsage(); // Refresh upload usage after upload
              }} 
            />

            {loading ? (
              <SkeletonRows label="Loading documents..." />
            ) : (
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Type</th>
                      <th>Owner</th>
                      {isSuperAdmin && <th>Tenant</th>}
                      <th>Company_ID</th>
                      <th>Vector Count</th>
                      <th>Upload Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.length === 0 ? (
                      <tr>
                        <td colSpan={isSuperAdmin ? 8 : 7} className="admin-empty">No documents found</td>
                      </tr>
                    ) : (
                      documents.map((doc, index) => (
                        <tr key={index}>
                          <td>
                            {canDownloadDocuments && doc.multerFilename ? (
                              <button
                                type="button"
                                className="admin-doc-download"
                                onClick={() => handleDownloadDocument(doc)}
                                title="Download"
                              >
                                <FiFileText aria-hidden="true" /> {doc.filename}
                              </button>
                            ) : (
                              <span className="admin-doc-name" title="download access restricted">
                                <FiFileText aria-hidden="true" /> {doc.filename}
                              </span>
                            )}
                          </td>
                          <td><span className="admin-pill is-blue">{doc.type?.toUpperCase() || 'UNKNOWN'}</span></td>
                          <td>{doc.ownerEmail || 'Unknown'}</td>
                          {isSuperAdmin && <td>{doc.tenantName || doc.companyId || 'N/A'}</td>}
                          <td>{doc.companyId || 'N/A'}</td>
                          <td>
                            {doc.vectorCount !== undefined ? (
                              <span title={doc.existsInQdrant === false ? 'Document not found in Qdrant database' : doc.qdrantVerified === false ? 'Verification failed' : 'Verified in Qdrant'}>
                                {doc.vectorCount}
                                {doc.existsInQdrant === false && (
                                  <span className="admin-vector-warn" aria-label="Missing from Qdrant"><FiAlertTriangle /></span>
                                )}
                                {doc.qdrantVerified === false && doc.existsInQdrant !== false && ' (?)'}
                              </span>
                            ) : doc.chunks ? (
                              <span title="Count from manifest (not verified)">{doc.chunks}</span>
                            ) : (
                              <span className="admin-empty">N/A</span>
                            )}
                          </td>
                          <td>{formatDate(doc.ingestDate)}</td>
                          <td>
                            <button
                              className="admin-delete-btn"
                              onClick={() => handleDeleteDocument(doc.filename, doc.companyId)}
                            >
                              <FiTrash2 aria-hidden="true" /> Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">User feedback &amp; reviews</h2>
                <p className="admin-section-subtitle">Ratings and comments from people using NovaBotX.</p>
              </div>
              <button
                className="admin-add-btn"
                onClick={loadFeedback}
                disabled={loading}
              >
                <FiRefreshCw aria-hidden="true" className={loading ? 'is-spinning' : ''} />
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {/* Vote Feedback Summary */}
            {voteFeedback && (
              <div className="admin-vote-summary">
                <h3 className="admin-vote-summary-title">Vote feedback summary</h3>
                <div className="admin-stats-grid">
                  <div className="admin-stat-item">
                    <span className="admin-stat-icon" aria-hidden="true"><FiBarChart2 /></span>
                    <span className="stat-label">Total votes</span>
                    <span className="stat-value"><AnimatedNumber value={voteFeedback.totalVotes || 0} /></span>
                  </div>
                  <div className="admin-stat-item">
                    <span className="admin-stat-icon tone-green" aria-hidden="true"><FiThumbsUp /></span>
                    <span className="stat-label">Upvotes</span>
                    <span className="stat-value tone-green"><AnimatedNumber value={voteFeedback.upVotes || 0} /></span>
                  </div>
                  <div className="admin-stat-item">
                    <span className="admin-stat-icon tone-red" aria-hidden="true"><FiThumbsDown /></span>
                    <span className="stat-label">Downvotes</span>
                    <span className="stat-value tone-red"><AnimatedNumber value={voteFeedback.downVotes || 0} /></span>
                  </div>
                  <div className="admin-stat-item">
                    <span className="admin-stat-icon tone-teal" aria-hidden="true"><FiZap /></span>
                    <span className="stat-label">Satisfaction</span>
                    <span className="stat-value tone-blue">
                      {voteFeedback.satisfactionFromVotes ? `${voteFeedback.satisfactionFromVotes.toFixed(1)}%` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Reviews List */}
            {loading ? (
              <SkeletonRows label="Loading feedback..." rows={3} />
            ) : feedback.length === 0 ? (
              <div className="admin-feedback-empty">
                <span className="admin-feedback-empty-icon" aria-hidden="true"><FiInbox /></span>
                <strong>No feedback reviews yet</strong>
                <span>User feedback will appear here once customers submit ratings and reviews</span>
              </div>
            ) : (
              <div className="admin-feedback-list nb-stagger">
                {feedback.map((review, index) => (
                  <div key={index} className="admin-feedback-card">
                    <div className="admin-feedback-card-head">
                      <div className="admin-feedback-rating">
                        <span className="admin-feedback-stars" aria-label={`${review.rating} out of 5 stars`}>
                          {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                        </span>
                        <span className="admin-feedback-score">{review.rating}/5</span>
                      </div>
                      <div className="admin-feedback-actions">
                        <span className="admin-feedback-date">
                          {new Date(review.ts).toLocaleString()}
                        </span>
                        <button
                          className="admin-delete-btn"
                          onClick={() => handleDeleteFeedback(index)}
                          title="Delete this feedback"
                        >
                          <FiTrash2 aria-hidden="true" /> Delete
                        </button>
                      </div>
                    </div>

                    {review.review && (
                      <blockquote className="admin-feedback-quote">
                        "{review.review}"
                      </blockquote>
                    )}

                    <div className="admin-feedback-meta">
                      <div>
                        <b>User:</b> {review.userName || 'Anonymous'}
                      </div>
                      {review.userEmail && (
                        <div>
                          <b>Email:</b> {review.userEmail}
                        </div>
                      )}
                      {review.sessionId && (
                        <div>
                          <b>Session:</b> {review.sessionId.substring(0, 8)}...
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            {feedback.length > 0 && (
              <div className="admin-feedback-footer">
                Showing {feedback.length} review{feedback.length !== 1 ? 's' : ''} (most recent first)
                <small>
                  Average Rating: {(feedback.reduce((sum, r) => sum + r.rating, 0) / feedback.length).toFixed(1)}/5.0
                </small>
              </div>
            )}
          </div>
        )}

        {activeTab === 'metrics' && (
          <MetricsDashboard
            isSuperAdmin={isSuperAdmin}
            selectedTenant={selectedTenant}
          />
        )}

        {activeTab === 'logo' && isAdmin && !isSuperAdmin && (
          <div className="admin-section admin-logo-section">
            <div className="admin-section-header">
              <div>
                <h2 className="admin-section-title">Brand logo</h2>
                <p className="admin-section-subtitle">
                  Customize the logo displayed in the NovaBotX chat sidebar.
                </p>
              </div>
            </div>

            <div className="logo-settings-grid">
              <section className="logo-card">
                <div className="logo-card-heading">
                  <span className="logo-card-kicker">Current</span>
                  <h3>Active logo</h3>
                </div>
                <div className="logo-preview">
                  {loadingLogo ? (
                    <div className="logo-loading">Loading logo…</div>
                  ) : (
                    <img
                      src={
                        currentLogoUrl
                          ? (currentLogoUrl.startsWith('http') ? currentLogoUrl : `${API_BASE_URL}${currentLogoUrl}`)
                          : `${import.meta.env.BASE_URL}assets/novalogo.png`
                      }
                      alt={currentLogoUrl ? 'Current organization logo' : 'Default NovaLink logo'}
                      className="current-logo-img"
                    />
                  )}
                </div>
                <p className="logo-card-note">
                  {currentLogoUrl ? 'Your organization logo is currently active.' : 'Using the default NovaLink logo.'}
                </p>
              </section>

              <section className="logo-card">
                <div className="logo-card-heading">
                  <span className="logo-card-kicker">Replace</span>
                  <h3>Upload a new logo</h3>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleLogoFileChange}
                  className="logo-file-input"
                  id="logo-file-input"
                />
                <label htmlFor="logo-file-input" className={`logo-drop-area ${logoPreview ? 'has-preview' : ''}`}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="Selected logo preview" className="logo-preview-img" />
                  ) : (
                    <>
                      <span className="logo-upload-icon" aria-hidden="true">
                        <FiUploadCloud size={24} />
                      </span>
                      <strong>Choose an image</strong>
                      <span>Preview it before saving</span>
                    </>
                  )}
                </label>
                <div className="logo-upload-meta">
                  <span>{logoFile ? logoFile.name : 'JPG, PNG, GIF or WebP'}</span>
                  <span>Maximum 5MB</span>
                </div>
                <div className="logo-actions">
                  {logoFile && (
                    <button
                      type="button"
                      className="logo-clear-btn"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                        const input = document.getElementById('logo-file-input');
                        if (input) input.value = '';
                      }}
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={handleSaveLogo}
                    disabled={!logoFile || loadingLogo}
                    className="logo-save-btn"
                  >
                    {loadingLogo ? 'Saving…' : 'Save logo'}
                  </button>
                </div>
              </section>
            </div>
          </div>
        )}
      </main>
          </div>
        </div>
      </div>

      {showSubscriptionCenter && (
        <SubscriptionCenterModal
          open={showSubscriptionCenter}
          initialTab={subscriptionCenterTab}
          onClose={() => setShowSubscriptionCenter(false)}
        />
      )}

      {touring && tourSteps.length > 0 && (
        <TourMode steps={tourSteps} topOffset={8} onClose={endTour} />
      )}
    </div>
  );
}

export default AdminDashboard;
